// src/DatabaseAdmin/DatabaseAdmin.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import { getAPIUrl, getAuthAPIUrl } from '../api/apiUtils';
import Sidebar from './Sidebar';
import Table from './Table';
import Modal from './Modal';
import Pagination from './Pagination';
import RegionsGroupsView from './RegionsGroupsView';
import styles from './DatabaseAdmin.module.scss';
import { apiFetch } from "../api/apiClient";

const API_BASE_URL = getAPIUrl();
const AUTH_API_BASE_URL = getAuthAPIUrl();

// Список страниц для доступа — соответствует Navbar.jsx
const PAGES = [
  "Collect",
  "SourceData",
  "ExportData",
  "PostsSearch",
  "Admin",
];

const DatabaseAdmin = () => {
  const [activeSection, setActiveSection] = useState('info'); // info | users | tables
  const [tables] = useState(['Регионы', 'Паблики']);
  const [selectedTable, setSelectedTable] = useState('');
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newRow, setNewRow] = useState({ name: '', group_id: '', city: '', region: '', title: '', region_id: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- Управление пользователями и доступом ---
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [accessList, setAccessList] = useState([]);
  const [newPassword, setNewPassword] = useState('');
  const [passwordChangeMsg, setPasswordChangeMsg] = useState('');
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityUserId, setActivityUserId] = useState('');
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);

  useEffect(() => {
    // Возвращаем старое поведение: список пользователей как раньше
    axios.get(`${API_BASE_URL}/users/users`)
      .then(res => setUsers(res.data))
      .catch(err => {
        console.error('Ошибка загрузки списка пользователей:', err);
        setUsers([]);
      });
  }, []);

  useEffect(() => {
    if (selectedUser) {
      // Готовый эндпоинт прав доступа к страницам: GET /users/user_access/<user_id>
      axios.get(`${API_BASE_URL}/users/user_access/${selectedUser.id}`)
        .then(res => {
          const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          setAccessList(list);
        })
        .catch(err => {
          console.error('Ошибка загрузки accessList:', err);
          setAccessList([]);
        });
    } else {
      setAccessList([]);
    }
  }, [selectedUser]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const setUserAccess = async (userId, page, canView, canEdit) => {
    // Готовый эндпоинт управления доступом к страницам: POST /users/user_access
    await axios.post(`${API_BASE_URL}/users/user_access`, { user_id: userId, page, can_view: canView, can_edit: canEdit });
    // обновить accessList после сохранения
    const res = await axios.get(`${API_BASE_URL}/users/user_access/${userId}`);
    const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
    setAccessList(list);
  };

  const deleteUserAccess = async (accessId, userId) => {
    // Готовый эндпоинт удаления настройки доступа: DELETE /users/user_access/<access_id>
    await axios.delete(`${API_BASE_URL}/users/user_access/${accessId}`);
    // обновить accessList после удаления
    const res = await axios.get(`${API_BASE_URL}/users/user_access/${userId}`);
    const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
    setAccessList(list);
  };

  const handleAccessChange = (page, value) => {
    setAccessList(prev => {
      const found = prev.find(a => a.page === page);
      if (found) {
        if (!value) {
          // Удаляем запрет (разрешаем доступ)
          if (found.id) deleteUserAccess(found.id, selectedUser.id);
          return prev.filter(a => a.page !== page);
        }
        // Уже запрещено, ничего не делаем
        return prev;
      } else {
        // Добавляем запрет и сразу сохраняем
        setUserAccess(selectedUser.id, page, true, false);
        return [...prev, { page, can_view: true }];
      }
    });
  };

  const handleAccessSave = (page) => {
    const access = accessList.find(a => a.page === page);
    if (access && access.can_view) {
      // Сохраняем запрет
      setUserAccess(selectedUser.id, page, true, false);
    } else {
      // Если галочка снята — удаляем запрет (разрешаем доступ)
      const found = accessList.find(a => a.page === page);
      if (found && found.id) {
        deleteUserAccess(found.id, selectedUser.id);
      }
    }
  };

  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const activityItemsPerPage = 25;
  const activityTotalPages = Math.max(1, Math.ceil(activityTotal / activityItemsPerPage));

  // При переходе в раздел таблиц — сразу показываем первую таблицу
  useEffect(() => {
    if (activeSection === 'tables' && !selectedTable && tables.length) {
      handleTableSelect(tables[0]);
    }
  }, [activeSection, selectedTable, tables]);

  useEffect(() => {
    if (activeSection !== 'tables' || !selectedTable) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const endpoint = selectedTable === 'Регионы' ? 'region' : 'publics';
        const response = await axios.get(
          `${API_BASE_URL}/crud/table/${endpoint}?page=${currentPage}&per_page=${itemsPerPage}`
        );
        
        const fetchedData = response.data.data || [];
        const total = response.data.total || 0;
        
        setData(fetchedData);
        setTotalItems(total);
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
        setData([]);
        setError('Ошибка загрузки данных: ' + (error.response?.data?.error || error.message));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeSection, selectedTable, currentPage]);

  const fetchActivityLogs = async () => {
    setActivityLoading(true);
    setActivityError(null);

    try {
      const params = new URLSearchParams({
        page: String(activityPage),
        per_page: String(activityItemsPerPage),
      });

      if (activityUserId) {
        params.set('user_id', activityUserId);
      }

      const response = await axios.get(`${API_BASE_URL}/users/request_logs?${params.toString()}`);
      setActivityLogs(response.data.data || []);
      setActivityTotal(response.data.total || 0);
    } catch (error) {
      console.error('Ошибка загрузки журнала активности:', error);
      setActivityLogs([]);
      setActivityTotal(0);
      setActivityError(error.response?.data?.error || error.message);
    } finally {
      setActivityLoading(false);
    }
  };

  const exportActivityLogs = async () => {
    setActivityError(null);

    try {
      const params = new URLSearchParams();
      if (activityUserId) {
        params.set('user_id', activityUserId);
      }

      const response = await axios.get(`${API_BASE_URL}/users/request_logs/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || 'user_request_logs.csv';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка экспорта журнала активности:', error);
      setActivityError(error.response?.data?.error || error.message);
    }
  };

  useEffect(() => {
    if (activeSection !== 'activity') return;
    fetchActivityLogs();
  }, [activeSection, activityPage, activityUserId]);

  const handleTableSelect = (table) => {
    setSelectedTable(table);
    setCurrentPage(1);
    setSelectedRow(null);
    setData([]);
    setError(null);
    if (table === 'Регионы') {
      setNewRow({ city: '', region: '' });
    } else if (table === 'Паблики') {
      setNewRow({ title: '', group_id: '', region_id: '' });
    } else {
      setNewRow({ name: '', group_id: '' });
    }
  };

  const handleAdd = () => {
    setIsModalOpen(true);
    setError(null);
    if (selectedTable === 'Регионы') {
      setNewRow({ city: '', region: '' });
    } else if (selectedTable === 'Паблики') {
      setNewRow({ title: '', group_id: '', region_id: '' });
    } else {
      setNewRow({ name: '', group_id: '' });
    }
  };

  const handleEdit = (row) => {
    setIsModalOpen(true);
    setError(null);
    setNewRow(row);
  };

  const handleSave = async () => {
    let cleanedNewRow;
    
    // Подготовка данных для отправки
    if (selectedTable === 'Регионы') {
      cleanedNewRow = { city: newRow.city, region: newRow.region };
    } else if (selectedTable === 'Паблики') {
      cleanedNewRow = { 
        title: newRow.title, 
        region_id: parseInt(newRow.region_id), 
        group_id: newRow.group_id || null 
      };
    }

    // Валидация
    if (selectedTable === 'Регионы' && (!cleanedNewRow.city || !cleanedNewRow.region)) {
      setError('Необходимо заполнить поля: Город, Регион');
      return;
    }
    if (selectedTable === 'Паблики' && (!cleanedNewRow.title || !cleanedNewRow.region_id)) {
      setError('Необходимо заполнить поля: Название, ID региона');
      return;
    }

    try {
      const endpoint = selectedTable === 'Регионы' ? 'region' : 'publics';
      const url = newRow.id 
        ? `${API_BASE_URL}/crud/table/${endpoint}/${newRow.id}`
        : `${API_BASE_URL}/crud/table/${endpoint}`;
      
      const method = newRow.id ? 'PUT' : 'POST';
      const request = axios({ method, url, data: cleanedNewRow });

      await request;
      
      // Обновляем данные после сохранения
      const fetchResponse = await axios.get(
        `${API_BASE_URL}/crud/table/${endpoint}?page=1&per_page=${itemsPerPage}`
      );
      
      const fetchedData = fetchResponse.data.data || [];
      const total = fetchResponse.data.total || 0;
      
      setData(fetchedData);
      setTotalItems(total);
      setCurrentPage(1);
      setIsModalOpen(false);
      setError(null);
    } catch (error) {
      console.error('Ошибка при сохранении:', error);
      setError(error.response?.data?.error || 'Ошибка при сохранении данных');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;

    try {
      const endpoint = selectedTable === 'Регионы' ? 'region' : 'publics';
      await axios.delete(`${API_BASE_URL}/crud/table/${endpoint}/${selectedRow}`);
      
      // Обновляем данные после удаления
      const response = await axios.get(
        `${API_BASE_URL}/crud/table/${endpoint}?page=1&per_page=${itemsPerPage}`
      );
      
      const fetchedData = response.data.data || [];
      const total = response.data.total || 0;
      
      setData(fetchedData);
      setTotalItems(total);
      setSelectedRow(null);
      setCurrentPage(1);
      setError(null);
    } catch (error) {
      console.error('Ошибка при удалении:', error);
      setError('Ошибка удаления данных: ' + (error.response?.data?.error || error.message));
    }
  };

  // Смена пароля выбранного пользователя (админ может менять без старого пароля)
  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) {
      setPasswordChangeMsg('Введите новый пароль');
      return;
    }
    try {
      const res = await apiFetch(`${AUTH_API_BASE_URL}/profile/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: selectedUser.id,
          old_password: 'admin_force', // спец. значение, на бэке можно разрешить для админа
          new_password: newPassword
        })
      });
      const data = await res.json();
      if (res.ok) {
        setPasswordChangeMsg('Пароль успешно изменён!');
        setNewPassword('');
      } else {
        setPasswordChangeMsg(data.error || 'Ошибка смены пароля');
      }
    } catch (e) {
      setPasswordChangeMsg('Ошибка соединения с сервером');
    }
  };

  const formatActivityDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('ru-RU');
  };

  return (
    <div className={styles.container_DatabaseAdmin}>
      <Sidebar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        tables={tables}
        selectedTable={selectedTable}
        onTableSelect={handleTableSelect}
        users={users}
        selectedUser={selectedUser}
        onUserSelect={handleUserSelect}
      />
      <div className={styles.main_DatabaseAdmin}>
        {activeSection === 'info' && (
          <div style={{ padding: 24 }}>
            <RegionsGroupsView />
          </div>
        )}

        {activeSection === 'users' && (
          <div style={{ padding: 24 }}>
            {!selectedUser && (
              <div style={{ color: '#4b5563' }}>
                Выберите пользователя слева, чтобы настроить доступ или сменить пароль.
              </div>
            )}
            {selectedUser && (
              <>
              <div style={{ margin: '0 0 24px 0', background: '#fff', borderRadius: 12, boxShadow: '0 0px 24px rgba(0,0,0,0.07)', padding: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Доступ пользователя: {selectedUser.username}</h2>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f3f4f6' }}>
                      <th style={{ padding: 8, textAlign: 'left' }}>Страница</th>
                      <th style={{ padding: 8 }}>Запретить доступ</th>
                      <th style={{ padding: 8 }}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PAGES.map(page => {
                      const access = accessList.find(a => a.page === page) || { can_view: false };
                      return (
                        <tr key={page} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: 8 }}>{page}</td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={!!access.can_view}
                              onChange={e => handleAccessChange(page, e.target.checked)}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {access.id && !!access.can_view && (
                              <button
                                className={styles.deleteButton_DatabaseAdmin}
                                style={{ margin: '0 0 0 8px', padding: '6px 16px', fontSize: 13, cursor: 'pointer', color: '#ef4444', background: '#fff' }}
                                onClick={() => handleAccessChange(page, false)}
                              >
                                Удалить
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 0px 12px rgba(0,0,0,0.04)', padding: 24, maxWidth: 400 }}>
                <h3 style={{ fontSize: 17, fontWeight: 500, marginBottom: 12 }}>Сменить пароль пользователя</h3>
                <input
                  type="password"
                  placeholder="Новый пароль"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, marginBottom: 8 }}
                />
                <button
                  className={styles.addButton_DatabaseAdmin}
                  style={{ width: '100%', padding: '10px', fontSize: 15 }}
                  onClick={handleChangePassword}
                >
                  Сменить пароль
                </button>
                {passwordChangeMsg && (
                  <div style={{ marginTop: 10, color: passwordChangeMsg.includes('успешно') ? 'green' : 'red' }}>{passwordChangeMsg}</div>
                )}
              </div>
              </>
            )}
          </div>
        )}

        {activeSection === 'activity' && (
          <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Журнал активности</h2>
                <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                  Кто, какой endpoint вызвал, когда, с каким статусом и IP.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  className={styles.addButton_DatabaseAdmin}
                  style={{ margin: 0, padding: '10px 18px' }}
                  onClick={exportActivityLogs}
                  disabled={activityLoading}
                >
                  Экспорт CSV
                </button>
                <button
                  className={styles.addButton_DatabaseAdmin}
                  style={{ margin: 0, padding: '10px 18px' }}
                  onClick={fetchActivityLogs}
                  disabled={activityLoading}
                >
                  Обновить
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <label style={{ color: '#374151', fontSize: 14 }}>Пользователь</label>
              <select
                value={activityUserId}
                onChange={(e) => {
                  setActivityUserId(e.target.value);
                  setActivityPage(1);
                }}
                style={{ minWidth: 240, padding: '9px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
              >
                <option value="">Все пользователи</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.username}</option>
                ))}
              </select>
            </div>

            {activityError && (
              <div style={{ color: '#b91c1c', background: '#fee2e2', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                Ошибка загрузки журнала: {activityError}
              </div>
            )}

            <div style={{ width: '100%', overflow: 'hidden', background: '#fff', borderRadius: 8, boxShadow: '0 0px 18px rgba(0,0,0,0.06)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 13 }}>
                <colgroup>
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '13%' }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#f3f4f6', color: '#374151' }}>
                    <th style={{ padding: 10, textAlign: 'left', whiteSpace: 'nowrap' }}>Время</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Пользователь</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Метод</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Path</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Endpoint</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>Статус</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>IP</th>
                    <th style={{ padding: 10, textAlign: 'left' }}>User-Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {activityLoading && (
                    <tr>
                      <td colSpan={8} style={{ padding: 18, textAlign: 'center', color: '#6b7280' }}>
                        Загрузка...
                      </td>
                    </tr>
                  )}

                  {!activityLoading && activityLogs.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ padding: 18, textAlign: 'center', color: '#6b7280' }}>
                        Записей пока нет.
                      </td>
                    </tr>
                  )}

                  {!activityLoading && activityLogs.map(log => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 10, whiteSpace: 'nowrap' }}>{formatActivityDate(log.created_at)}</td>
                      <td style={{ padding: 10, whiteSpace: 'nowrap' }}>{log.username} #{log.user_id}</td>
                      <td style={{ padding: 10, fontWeight: 600 }}>{log.method}</td>
                      <td title={log.path} style={{ padding: 10, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{log.path}</td>
                      <td title={log.endpoint || '-'} style={{ padding: 10, overflowWrap: 'anywhere', wordBreak: 'break-word', color: '#4b5563' }}>{log.endpoint || '-'}</td>
                      <td style={{ padding: 10 }}>{log.status_code}</td>
                      <td style={{ padding: 10, overflowWrap: 'anywhere' }}>{log.ip || '-'}</td>
                      <td title={log.user_agent || '-'} style={{ padding: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#4b5563' }}>{log.user_agent || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 16 }}>
              <div style={{ color: '#6b7280', fontSize: 13 }}>
                Всего записей: {activityTotal}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  className={styles.deleteButton_DatabaseAdmin}
                  style={{ margin: 0, padding: '8px 14px', cursor: activityPage <= 1 ? 'not-allowed' : 'pointer' }}
                  onClick={() => setActivityPage(page => Math.max(1, page - 1))}
                  disabled={activityPage <= 1 || activityLoading}
                >
                  Назад
                </button>
                <span style={{ color: '#374151', fontSize: 14 }}>
                  {activityPage} / {activityTotalPages}
                </span>
                <button
                  className={styles.deleteButton_DatabaseAdmin}
                  style={{ margin: 0, padding: '8px 14px', cursor: activityPage >= activityTotalPages ? 'not-allowed' : 'pointer' }}
                  onClick={() => setActivityPage(page => Math.min(activityTotalPages, page + 1))}
                  disabled={activityPage >= activityTotalPages || activityLoading}
                >
                  Вперёд
                </button>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'tables' && selectedTable && (
          <>
            <div className={styles.actions_DatabaseAdmin}>
              <button className={styles.addButton_DatabaseAdmin} onClick={handleAdd}>
                Добавить
              </button>
              <button
                className={`${styles.deleteButton_DatabaseAdmin} ${selectedRow ? styles.active_DatabaseAdmin : ''}`}
                onClick={handleDelete}
                disabled={!selectedRow}
              >
                Удалить
              </button>
            </div>
            <Table
              selectedTable={selectedTable}
              data={data}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
              onEdit={handleEdit}
              loading={loading}
              error={error}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              loading={loading}
            />
            <Modal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              selectedTable={selectedTable}
              newRow={newRow}
              setNewRow={setNewRow}
              onSave={handleSave}
              error={error}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DatabaseAdmin;
